import { useState } from 'react';
import { useSendRecoveryCodeMutation, useConfirmRecoveryCodeMutation } from '@/shared/api/auth';
import { getApiErrorString, useCountdown } from '@/shared/utils';

const RESEND_COOLDOWN_SECONDS = 30;

export function useResetPassword() {
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isCodeConfirmed, setIsCodeConfirmed] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const { secondsLeft: resendCountdown, start: startResendCooldown } = useCountdown();

  const {
    sendRecoveryCode: sendRecoveryCodeRequest,
    isSendingRecoveryCode,
    resetSendRecoveryCode,
    sendRecoveryCodeError,
  } = useSendRecoveryCodeMutation();
  const {
    confirmRecoveryCode: confirmRecoveryCodeRequest,
    isConfirmingRecoveryCode,
    resetConfirmRecoveryCode,
    confirmRecoveryCodeError,
  } = useConfirmRecoveryCodeMutation();

  const sendRecoveryCode = async (email: string) => {
    resetSendRecoveryCode();
    await sendRecoveryCodeRequest({ email });

    setEmail(email);
    setIsCodeSent(true);
    startResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  // A confirmed code advances to the in-wizard "set a new password" step (no redirect): the email +
  // code are held in state and handed to `SetNewPasswordForm`.
  const confirmCode = async (code: string) => {
    resetConfirmRecoveryCode();
    await confirmRecoveryCodeRequest({ email, recoveryCode: code });

    setCode(code);
    setIsCodeConfirmed(true);
  };

  const resendCode = async () => {
    resetSendRecoveryCode();
    await sendRecoveryCodeRequest({ email });
    startResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const reset = () => {
    setIsCodeSent(false);
    setEmail('');
  };

  return {
    sendRecoveryCode,
    confirmCode,
    resendCode,
    reset,
    isCodeSent,
    isCodeConfirmed,
    email,
    code,
    resendCountdown,
    isSendingRecoveryCode,
    isConfirmingRecoveryCode,
    sendRecoveryCodeError: sendRecoveryCodeError && getApiErrorString(sendRecoveryCodeError),
    confirmRecoveryCodeError: confirmRecoveryCodeError && getApiErrorString(confirmRecoveryCodeError),
  };
}
